/**
 * NotificationPanel
 * Dropdown list of the current user's notifications, opened from the navbar bell.
 * Fetches on open, supports mark-one-read and mark-all-read, and reports the
 * resulting unread count back so the bell badge stays in sync.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  ActivityIndicator,
  Platform,
} from 'react-native';
import Svg, { Path } from 'react-native-svg';
import { COLORS, FONTS } from '../constants/config';
import { notificationsApi } from '../services/apiService';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';

interface NotificationItem {
  id: number;
  title: string;
  message: string;
  notification_type?: string;
  is_read: boolean;
  created_at: string;
}

interface NotificationPanelProps {
  visible: boolean;
  onClose: () => void;
  onCountChange?: (unread: number) => void;
}

const CheckIcon = ({ size = 16, color = COLORS.iconMuted }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M20 6L9 17L4 12" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </Svg>
);

function useRelativeTime() {
  const { t } = useLanguage();
  return useCallback(
    (iso: string): string => {
      const then = new Date(iso).getTime();
      if (Number.isNaN(then)) return '';
      const diffMin = Math.floor((Date.now() - then) / 60000);
      if (diffMin < 1) return t('notifications.justNow');
      if (diffMin < 60) return t('notifications.minutesAgo', { count: diffMin });
      const diffHr = Math.floor(diffMin / 60);
      if (diffHr < 24) return t('notifications.hoursAgo', { count: diffHr });
      const diffDay = Math.floor(diffHr / 24);
      return t('notifications.daysAgo', { count: diffDay });
    },
    [t],
  );
}

export default function NotificationPanel({ visible, onClose, onCountChange }: NotificationPanelProps) {
  const { colors } = useTheme();
  const { t } = useLanguage();
  const relTime = useRelativeTime();
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(false);

  const emitCount = useCallback(
    (list: NotificationItem[]) => {
      onCountChange?.(list.filter((n) => !n.is_read).length);
    },
    [onCountChange],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await notificationsApi.list();
      const list: NotificationItem[] = Array.isArray(data) ? data : data?.results || [];
      setItems(list);
      emitCount(list);
    } catch {
      // Silent — bell already tolerates fetch failures
    } finally {
      setLoading(false);
    }
  }, [emitCount]);

  useEffect(() => {
    if (visible) load();
  }, [visible, load]);

  const handleItemPress = async (item: NotificationItem) => {
    if (item.is_read) return;
    // Optimistic
    const next = items.map((n) => (n.id === item.id ? { ...n, is_read: true } : n));
    setItems(next);
    emitCount(next);
    try {
      await notificationsApi.markRead(item.id);
    } catch {
      load(); // revert to server truth on failure
    }
  };

  const handleMarkAllRead = async () => {
    const next = items.map((n) => ({ ...n, is_read: true }));
    setItems(next);
    emitCount(next);
    try {
      await notificationsApi.markAllRead();
    } catch {
      load();
    }
  };

  if (!visible) return null;

  const hasUnread = items.some((n) => !n.is_read);

  return (
    <View style={[styles.panel, { backgroundColor: colors.card, borderColor: colors.cardBorder }]}>
      {/* Header */}
      <View style={[styles.header, { borderBottomColor: colors.cardBorder }]}>
        {hasUnread ? (
          <TouchableOpacity style={styles.markAllBtn} onPress={handleMarkAllRead}>
            <CheckIcon size={14} color={colors.primary} />
            <Text style={[styles.markAllText, { color: colors.primary }]}>{t('notifications.markAllRead')}</Text>
          </TouchableOpacity>
        ) : (
          <View />
        )}
        <Text style={[styles.headerTitle, { color: colors.textPrimary }]}>{t('notifications.title')}</Text>
      </View>

      {/* Body */}
      {loading ? (
        <View style={styles.center}>
          <ActivityIndicator size="small" color={colors.primary} />
        </View>
      ) : items.length === 0 ? (
        <View style={styles.center}>
          <Text style={[styles.emptyText, { color: colors.textLight }]}>{t('notifications.empty')}</Text>
        </View>
      ) : (
        <ScrollView style={styles.list} showsVerticalScrollIndicator={false}>
          {items.map((item) => (
            <TouchableOpacity
              key={item.id}
              style={[
                styles.item,
                { borderBottomColor: colors.cardBorder },
                !item.is_read && { backgroundColor: colors.gray50 },
              ]}
              onPress={() => handleItemPress(item)}
              activeOpacity={0.7}
            >
              <View style={styles.itemRow}>
                {!item.is_read && <View style={[styles.unreadDot, { backgroundColor: colors.primary }]} />}
                <View style={styles.itemContent}>
                  <Text style={[styles.itemTitle, { color: colors.textPrimary }]} numberOfLines={1}>
                    {item.title}
                  </Text>
                  <Text style={[styles.itemMessage, { color: colors.textMuted }]} numberOfLines={2}>
                    {item.message}
                  </Text>
                  <Text style={[styles.itemTime, { color: colors.textLight }]}>{relTime(item.created_at)}</Text>
                </View>
              </View>
            </TouchableOpacity>
          ))}
        </ScrollView>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  panel: {
    position: 'absolute',
    top: '100%',
    left: 0,
    marginTop: 8,
    width: 340,
    maxHeight: 420,
    borderWidth: 1,
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.12,
    shadowRadius: 16,
    elevation: 6,
    zIndex: 400,
    overflow: 'hidden',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 14,
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  headerTitle: {
    fontSize: 15,
    fontFamily: FONTS.medium,
    textAlign: 'right',
  },
  markAllBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    ...(Platform.OS === 'web' ? ({ cursor: 'pointer' } as any) : {}),
  },
  markAllText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
  },
  center: {
    paddingVertical: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
  },
  list: {
    maxHeight: 360,
  },
  item: {
    paddingHorizontal: 14,
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  itemRow: {
    flexDirection: 'row-reverse',
    alignItems: 'flex-start',
    gap: 8,
  },
  unreadDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginTop: 6,
  },
  itemContent: {
    flex: 1,
  },
  itemTitle: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    textAlign: 'right',
  },
  itemMessage: {
    fontSize: 13,
    fontFamily: FONTS.regular,
    textAlign: 'right',
    marginTop: 2,
    lineHeight: 18,
  },
  itemTime: {
    fontSize: 11,
    fontFamily: FONTS.regular,
    textAlign: 'right',
    marginTop: 4,
  },
});
